import './ScreenShareHintCard.scss';
import { ReactElement, useId } from 'react';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- карточка-подсказка у пункта меню со своей разметкой: AnchoredDialog — окно с ловушкой фокуса и Escape, OnboardingHint — только заголовок и текст
import { CloseButton, Popover } from '@mantine/core';
import { MdOutlinePanTool, MdOutlineStopCircle, MdOutlineVisibility } from 'react-icons/md';
import { ANCHORED_DIALOG_Z_INDEX } from '@srm/ui/lib/zIndex';
import { usePortalTarget } from '@srm/ui/provider';
import Button from '@srm/ui/button';
import SupportShareScheme from '@molecules/SupportShareScheme';

export interface IScreenShareHintCardProps {
    opened: boolean;
    /** Пункт «Показать экран поддержке»: элемент, принимающий ref */
    children: ReactElement;
    /** «Понятно» и крестик: подсказка пройдена, меню остаётся открытым */
    onDismiss: () => void;
    /** «Показать экран» — то же, что выбрать пункт */
    onShare: () => void;
    /**
     * Нажатие внутри карточки. Карточка в портале, вне панели меню: меню считает такое нажатие щелчком мимо — по нему
     * вызывающий не закрывает меню
     */
    onPressInside?: () => void;
}

const FACTS = [
    {
        icon: <MdOutlineVisibility size={18} aria-hidden />,
        text: 'Специалист видит только эту вкладку',
        detail: 'Не другие вкладки и программы. Видео не записывается.',
    },
    {
        icon: <MdOutlinePanTool size={18} aria-hidden />,
        text: 'Нажимать за вас — только с разрешения',
        detail: 'Забрать управление — Esc.',
    },
    {
        icon: <MdOutlineStopCircle size={18} aria-hidden />,
        text: '«Остановить показ» — в любой момент',
        detail: 'Кнопка в полосе сверху.',
    },
];

const TRANSITION = { transition: 'pop', duration: 200, timingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)' } as const;

/**
 * Второй шаг подсказки про показ экрана: карточка у пункта «Показать экран поддержке» в открытом меню «Помощь» — схема
 * «специалист видит только эту вкладку» и три факта. Не диалог: фокус не забирает, закрывается вместе с меню.
 */
const ScreenShareHintCard = ({ opened, children, onDismiss, onShare, onPressInside }: IScreenShareHintCardProps) => {
    const titleId = useId();
    const portalTarget = usePortalTarget();

    return (
        <Popover
            opened={opened}
            // Верх карточки — чуть выше пункта (схема над фактами), стрелка — на пункт; у края экрана — сдвиг
            position="left-start"
            withArrow
            arrowSize={12}
            arrowRadius={3}
            arrowPosition="center"
            offset={{ mainAxis: 26, crossAxis: -56 }}
            shadow="none"
            radius="md"
            zIndex={ANCHORED_DIALOG_Z_INDEX + 1}
            closeOnClickOutside={false}
            closeOnEscape={false}
            trapFocus={false}
            returnFocus={false}
            middlewares={{ flip: true, shift: { padding: 8 } }}
            classNames={{ dropdown: 'ss-hint-card', arrow: 'ss-hint-card__arrow' }}
            transitionProps={TRANSITION}
            portalProps={portalTarget ? { target: portalTarget } : undefined}
        >
            <Popover.Target>{children}</Popover.Target>
            <Popover.Dropdown
                role="dialog"
                aria-labelledby={titleId}
                data-testid="screen-share-hint-card"
                data-remote-control-exclude
                // Портал — потомок пункта меню в дереве React: щелчок по карточке не должен выбрать пункт.
                onClick={(event) => event.stopPropagation()}
                onMouseDown={onPressInside}
                onTouchStart={onPressInside}
            >
                <SupportShareScheme scheme="only-tab" className="ss-hint-card__scheme" />
                <div className="ss-hint-card__inner">
                    <span className="ss-hint-card__step">2 из 2</span>
                    <h3 id={titleId} className="ss-hint-card__title">
                        Как это работает
                    </h3>
                    <ul className="ss-hint-card__facts">
                        {FACTS.map((fact) => (
                            <li key={fact.text}>
                                {fact.icon}
                                <span>
                                    {fact.text}
                                    <small>{fact.detail}</small>
                                </span>
                            </li>
                        ))}
                    </ul>
                    <div className="ss-hint-card__actions">
                        <Button variant="text" size="sm" data-testid="screen-share-hint-dismiss" onClick={onDismiss}>
                            Понятно
                        </Button>
                        <Button size="sm" data-testid="screen-share-hint-share" onClick={onShare}>
                            Показать экран
                        </Button>
                    </div>
                </div>
                <CloseButton
                    className="ss-hint-card__close"
                    size="sm"
                    aria-label="Закрыть подсказку"
                    onClick={onDismiss}
                />
            </Popover.Dropdown>
        </Popover>
    );
};

export default ScreenShareHintCard;
